import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ScanLine, AlertTriangle, Mail, Info } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api, fmtCHF, fmtDate } from "@/lib/api";
import { LEVEL_COLORS, INTAKE_STATUS, DOC_STATUS, TRUST } from "@/lib/organs";
import { useCompany } from "@/context/CompanyContext";
import { OrganIcon, SectionTitle, DemoBanner } from "@/components/Shared";
import { CENSUS_TO_UI, CENS_STATES, SYNC_PENDING } from "@/components/CensimentoFigure";
import CensusBasisPanel from "@/components/CensusBasisPanel";

function ListSection({ section }) {
  return (
    <div className="glass rounded-3xl p-5">
      <SectionTitle>{section.title}</SectionTitle>
      <div className="space-y-1">
        {section.items.map((it, i) => (
          <div key={i} className="flex items-center justify-between gap-3 rounded-2xl px-3 py-2.5 hover:bg-white/[0.02]"
            style={it.level ? { borderLeft: `2px solid ${LEVEL_COLORS[it.level]}` } : {}}>
            <div className="min-w-0">
              <div className="text-sm text-gray-200">{it.label}</div>
              {it.sub && <div className="text-xs text-gray-500">{it.sub}</div>}
            </div>
            {it.value && <div className="text-sm font-mono shrink-0" style={it.level ? { color: LEVEL_COLORS[it.level] } : {}}>{it.value}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ForecastSection({ section }) {
  return (
    <div className="glass rounded-3xl p-5" data-testid="forecast-section">
      <SectionTitle>{section.title}</SectionTitle>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={section.points} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id="liq" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.04)" />
            <XAxis dataKey="label" tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "#6B7280", fontSize: 10 }} axisLine={false} tickLine={false}
              tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <Tooltip contentStyle={{ background: "#0A0F1D", border: "1px solid rgba(0,229,255,0.2)", borderRadius: 12, fontSize: 12 }}
              formatter={(v) => [fmtCHF(v), "Liquidità"]} />
            <Area type="monotone" dataKey="value" stroke="#00E5FF" strokeWidth={2} fill="url(#liq)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {section.note && (
        <div className="mt-3 text-xs text-amber-300/90 bg-amber-400/5 border border-amber-400/15 rounded-xl px-3 py-2 flex items-start gap-2">
          <AlertTriangle size={13} className="shrink-0 mt-0.5" /> {section.note}
        </div>
      )}
    </div>
  );
}

export default function OrganPage({ organId }) {
  const [organ, setOrgan] = useState(null);
  const [intake, setIntake] = useState(null);
  const [moduleData, setModuleData] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [error, setError] = useState(false);
  const [basisOpen, setBasisOpen] = useState(false);
  const { company } = useCompany();

  useEffect(() => {
    setOrgan(null);
    setError(false);
    setIntake(null);
    setModuleData(null);
    setCompanyInfo(null);
    api.get(`/organs/${organId}`).then((r) => setOrgan(r.data)).catch(() => setError(true));
    api.get(`/modules/${organId}`).then((r) => setModuleData(r.data)).catch(() => {});
    api.get("/company").then((r) => { setCompanyInfo(r.data); setIntake(r.data?.intake || null); }).catch(() => {});
  }, [organId]);

  const intakeSt = INTAKE_STATUS[intake?.status] || INTAKE_STATUS.planned;
  const isDemo = companyInfo?.demo ?? company?.demo;
  const liveDocs = moduleData?.documents || [];
  const preAccounting = moduleData?.pre_accounting || [];
  // DG-PC-04 (GO utente 15.09.2026): display source = proiezione census (census_*), MAI legacy.
  // Fallback esplicito SYNC_PENDING (tecnico) — nessun silent fallback a status/coverage legacy.
  const censSt = CENS_STATES[CENSUS_TO_UI[organ?.census_state]] || SYNC_PENDING;
  const censCoverage = organ?.census_coverage ?? null;  // UNKNOWN ≠ 0%

  if (error) return <div className="max-w-3xl mx-auto px-4 text-gray-500">Organo non trovato.</div>;
  if (!organ) return <div className="max-w-3xl mx-auto px-4 text-gray-600 animate-pulse">DAD sta collegando l'organo…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4" data-testid={`organ-page-${organId}`}>
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-cyan-300 mb-4"><ChevronLeft size={14} /> Organismo</Link>

      <div className="glass rounded-3xl p-6 mb-5 fade-up">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <span className="w-14 h-14 rounded-3xl flex items-center justify-center text-cyan-300"
              style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.2)", boxShadow: "0 0 24px rgba(0,229,255,0.15)" }}>
              <OrganIcon icon={organ.icon} size={26} />
            </span>
            <div>
              <p className="text-xs tracking-[0.25em] text-gray-500 uppercase">{organ.anatomical_name} · metafora organismo</p>
              <h1 className="font-display text-2xl font-bold" data-testid="organ-title">{organ.business_name || organ.business_function}</h1>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            {/* DG-PC-04: badge dai 5 stati canonici census (label business-first), MAI StatusBadge legacy */}
            <span data-testid="organ-census-state"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
              style={{ color: censSt.color, background: `${censSt.color}18`, border: `1px solid ${censSt.color}35` }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: censSt.color }} />
              {censSt.label}
            </span>
            {isDemo && <DemoBanner />}
          </div>
        </div>
        <p className="text-sm text-gray-400 mt-4">{organ.tagline}</p>
        <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
          <span>Copertura censimento</span>
          {censCoverage == null ? (
            <span className="font-medium" style={{ color: SYNC_PENDING.color }} data-testid="organ-coverage">
              Sincronizzazione in corso
            </span>
          ) : (
            <>
              <div className="flex-1 h-1 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${censCoverage}%`, background: "linear-gradient(90deg,#3B82F6,#00E5FF)" }} />
              </div>
              <span data-testid="organ-coverage">{Math.round(censCoverage)}%</span>
              {organ.census_qualified && (
                <span className="text-amber-400 font-medium" data-testid="organ-coverage-qualified">· qualificata</span>
              )}
            </>
          )}
          <button type="button" aria-label="Perché questa percentuale?"
            data-testid="organ-basis-open"
            onClick={() => setBasisOpen(true)}
            className="ml-1 w-5 h-5 rounded-full flex items-center justify-center text-gray-500 hover:text-cyan-300 transition-colors"
            style={{ background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.15)" }}>
            <Info size={10} />
          </button>
        </div>
      </div>

      {organId === "eyes_ears" && (
        <div className="grid grid-cols-2 gap-3 mb-5 fade-up">
          <Link to="/scan" data-testid="intake-scan-cta" className="btn-primary !py-4"><ScanLine size={18} /> SCANSIONA</Link>
          <Link to="/scan-bulk" data-testid="intake-bulk-cta" className="btn-ghost !py-4">Bulk Scan</Link>
        </div>
      )}

      {organId === "eyes_ears" && intake && (
        <div className="glass rounded-3xl p-5 mb-5 fade-up" data-testid="intake-email-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Mail size={18} className="text-cyan-300" />
              <div>
                <div className="text-sm font-semibold">Email DAD Intake</div>
                <div className="text-xs text-gray-500 font-mono">{intake.reserved_address}</div>
              </div>
            </div>
            <span className="chip" style={{ color: intakeSt.color, borderColor: `${intakeSt.color}40`, background: `${intakeSt.color}12` }}
              data-testid="intake-email-status">{intakeSt.label}</span>
          </div>
          <p className="text-[11px] text-gray-600 mt-3 leading-relaxed">
            Ogni azienda avrà un indirizzo DAD Intake univoco: messaggi e allegati entreranno nello stesso
            motore canonico di DAD Scan. L'infrastruttura di ricezione non è ancora attiva — l'indirizzo è riservato.
          </p>
        </div>
      )}

      {organ.metrics.length > 0 && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="organ-metrics">
        {organ.metrics.map((mt) => (
          <div key={mt.label} className="glass rounded-2xl p-4">
            <div className="font-display font-bold text-lg leading-tight"
              style={mt.level ? { color: LEVEL_COLORS[mt.level] } : {}}>{mt.value}</div>
            <div className="text-[10px] text-gray-500 mt-1 leading-tight">{mt.label}</div>
          </div>
        ))}
      </div>
      )}

      {organ.census_state === "NON_ANALIZZATA" && organ.metrics.length === 0 && liveDocs.length === 0 && (
        <div className="glass rounded-3xl p-8 mb-5 text-center fade-up" data-testid="organ-insufficient">
          <p className="text-gray-400 text-sm font-medium">DATI INSUFFICIENTI</p>
          <p className="text-xs text-gray-600 mt-2 leading-relaxed">
            DAD non inventa informazioni. Quest'area si popolerà solo con dati verificati
            forniti da questa azienda (documenti, scansioni, integrazioni).
          </p>
        </div>
      )}

      {organId === "circulation" && preAccounting.length > 0 && (
        <div className="glass rounded-3xl p-5 mb-4" data-testid="pre-accounting-section">
          <SectionTitle>Documenti da contabilizzare · pre-contabilità</SectionTitle>
          <p className="text-[11px] text-gray-600 mb-3 -mt-1">Documenti verificati in attesa del futuro motore contabile. Nulla è marcato «contabilizzato».</p>
          <div className="space-y-1">
            {preAccounting.map((d) => (
              <div key={d.doc_id} data-testid={`pre-accounting-${d.doc_id}`} className="flex items-center justify-between gap-3 rounded-2xl px-3 py-2.5 hover:bg-white/[0.02]">
                <div className="min-w-0">
                  <div className="text-sm text-gray-200 truncate">{d.supplier || d.filename}</div>
                  <div className="text-xs text-gray-500">{d.doc_type} · {fmtDate(d.date)}{d.category ? ` · ${d.category}` : ""}{d.vat != null ? ` · IVA ${fmtCHF(d.vat)}` : ""}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="chip !text-[10px]" style={{ color: TRUST[d.trust]?.color }}>{TRUST[d.trust]?.label}</span>
                  <span className="chip !text-[10px] !text-amber-300 !border-amber-400/25 !bg-amber-400/5">Da contabilizzare</span>
                  <span className="font-mono text-sm">{fmtCHF(d.amount)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {liveDocs.length > 0 && (
        <div className="glass rounded-3xl p-5 mb-4" data-testid="organ-live-docs">
          <SectionTitle>Documenti collegati · una sola fonte originale</SectionTitle>
          <div className="space-y-1">
            {liveDocs.map((d) => {
              const st = DOC_STATUS[d.status];
              return (
                <Link to="/archive" key={d.doc_id} data-testid={`live-doc-${d.doc_id}`}
                  className="flex items-center justify-between gap-3 rounded-2xl px-3 py-2.5 hover:bg-white/[0.03] transition-colors">
                  <div className="min-w-0">
                    <div className="text-sm text-gray-200 truncate">{d.supplier || d.filename}</div>
                    <div className="text-xs text-gray-500">{d.doc_type} · {fmtDate(d.date)}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {st && <span className="text-[10px]" style={{ color: st.color }}>{st.label}</span>}
                    <span className="font-mono text-sm">{fmtCHF(d.amount)}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {organ.sections.map((s) =>
          s.kind === "forecast" ? <ForecastSection key={s.title} section={s} /> : <ListSection key={s.title} section={s} />
        )}
      </div>

      {organ.recommendations?.length > 0 && (
        <div className="mt-5 space-y-3" data-testid="organ-recommendations">
          <SectionTitle>DAD propone</SectionTitle>
          {organ.recommendations.map((r, i) => (
            <Link to="/actions" key={i} data-testid={`organ-rec-${i}`}
              className="glass rounded-3xl p-4 flex items-start gap-3 hover:border-cyan-400/30 transition-colors block">
              <span className="mt-1 w-2.5 h-2.5 rounded-full shrink-0"
                style={{ background: LEVEL_COLORS[r.level], boxShadow: `0 0 10px ${LEVEL_COLORS[r.level]}` }} />
              <span className="text-sm text-gray-300 leading-relaxed">{r.text}</span>
            </Link>
          ))}
        </div>
      )}

      {basisOpen && (
        <CensusBasisPanel organId={organId}
          organName={organ.business_name || organ.business_function}
          onClose={() => setBasisOpen(false)} />
      )}
    </div>
  );
}
